import { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedApi } from './api';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import LoadingSpinner from './components/LoadingSpinner';
import { countryCodes } from './data/countryCodes';
import './Profile.css';

export default function Profile() {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedApi();
  
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState(null);
  const [successField, setSuccessField] = useState(null);
  
  // Profile picture states
  const [profilePicture, setProfilePicture] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [loadingPicture, setLoadingPicture] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form fields from profiles table
  const [displayName, setDisplayName] = useState('');
  const [twitterHandle, setTwitterHandle] = useState('');
  const [blueskyHandle, setBlueskyHandle] = useState('');
  const [instagramHandle, setInstagramHandle] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [bio, setBio] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  
  // Original values to track changes
  const [originalValues, setOriginalValues] = useState({});
  
  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});
  
  // Saving states for each field
  const [savingField, setSavingField] = useState(null);

  // TipTap editor configuration
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable the default Link extension from StarterKit to avoid duplication
        link: false,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'Tell us about yourself...',
      }),
    ],
    content: bio,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      handleFieldChange('bio', html, setBio);
    },
  });


  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Load profile data - completely separate useEffect
  useEffect(() => {
    const loadProfileData = async () => {
      if (!isAuthenticated || !user?.email) {
        setLoadingProfile(false);
        return;
      }
      
      try {
        setLoadingProfile(true);
        const profileRes = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/members/profile?email=${encodeURIComponent(user.email)}`);
        
        if (profileRes.ok) {
          const data = await profileRes.json();
          
          // Populate form fields from profiles table
          const values = {
            display_name: data.display_name || '',
            twitter_handle: data.twitter_handle || '',
            bluesky_handle: data.bluesky_handle || '',
            instagram_handle: data.instagram_handle || '',
            contact_email: data.contact_email || '',
            bio: data.bio || '',
            phone: data.phone || ''
          };
          
          setDisplayName(values.display_name);
          setTwitterHandle(values.twitter_handle);
          setBlueskyHandle(values.bluesky_handle);
          setInstagramHandle(values.instagram_handle);
          setContactEmail(values.contact_email);
          setBio(values.bio);
          
          // Parse phone number to extract country code if present
          if (values.phone) {
            const phoneStr = values.phone.toString();
            // Try to find a matching country code at the start
            let foundCode = false;
            for (const cc of countryCodes) {
              if (phoneStr.startsWith(cc.code)) {
                setCountryCode(cc.code);
                setPhone(phoneStr.substring(cc.code.length).trim());
                foundCode = true;
                break;
              }
            }
            if (!foundCode) {
              setPhone(phoneStr);
            }
          } else {
            // Ensure phone is empty if no value exists
            setPhone('');
          }
          
          setOriginalValues(values);
        } else {
          throw new Error('Failed to load profile');
        }
        
      } catch (err) {
        setError(err.message || 'Failed to load profile');
      } finally {
        setLoadingProfile(false);
      }
    };
    
    loadProfileData();
  }, [isAuthenticated, user, authenticatedFetch]);

  // Load profile picture - completely separate useEffect
  useEffect(() => {
    const loadProfilePicture = async () => {
      if (!isAuthenticated || !user?.email) {
        return;
      }
      
      try {
        setLoadingPicture(true);
        const pictureRes = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/members/profile/picture?email=${encodeURIComponent(user.email)}`);
        
        if (pictureRes.ok) {
          const blob = await pictureRes.blob();
          const imageUrl = URL.createObjectURL(blob);
          setProfilePicture(imageUrl);
        }
      } catch (err) {
        // Error loading profile picture - silently fail
      } finally {
        setLoadingPicture(false);
      }
    };
    
    loadProfilePicture();
  }, [isAuthenticated, user, authenticatedFetch]);

  // Update editor content when bio changes from outside (e.g., loading from server)
  useEffect(() => {
    if (editor && bio !== editor.getHTML()) {
      editor.commands.setContent(bio);
    }
  }, [bio, editor]);

  const updateField = async (fieldName, fieldValue) => {
    setError(null);
    setSavingField(fieldName);

    // Add @ to twitter handle if it doesn't start with one
    if (fieldName === 'twitter_handle' && fieldValue && !fieldValue.startsWith('@')) {
      fieldValue = '@' + fieldValue;
      setTwitterHandle(fieldValue);
    }

    // Add @ to bluesky handle if it doesn't start with one
    if (fieldName === 'bluesky_handle' && fieldValue && !fieldValue.startsWith('@')) {
      fieldValue = '@' + fieldValue;
      setBlueskyHandle(fieldValue);
    }

    // Add @ to instagram handle if it doesn't start with one
    if (fieldName === 'instagram_handle' && fieldValue && !fieldValue.startsWith('@')) {
      fieldValue = '@' + fieldValue;
      setInstagramHandle(fieldValue);
    }

    // Combine country code and phone number for phone field
    if (fieldName === 'phone') {
      fieldValue = fieldValue ? `${countryCode}${fieldValue}` : '';
    }

    try {
      const res = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/members/profile`, {
        method: 'PUT',
        body: JSON.stringify({
          email: user.email,
          [fieldName]: fieldValue
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to update profile');
      }

      // Update original value and set success state
      setOriginalValues(prev => ({ ...prev, [fieldName]: fieldValue }));
      setSuccessField(fieldName);
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSavingField(null);
    }
  };

  // Check if field has changed from original
  const hasChanged = (fieldName, currentValue) => {
    // For phone, compare the combined value
    if (fieldName === 'phone') {
      const combinedValue = currentValue ? `${countryCode}${currentValue}` : '';
      return combinedValue !== originalValues[fieldName];
    }
    return currentValue !== originalValues[fieldName];
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        setProfilePicture(dataUrl);
        uploadProfilePicture(file, dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload profile picture
  const uploadProfilePicture = async (file, dataUrl) => {
    setUploadingPicture(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('picture', file);
      formData.append('email', user.email);
      
      const res = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/members/profile/picture`, {
        method: 'POST',
        body: formData
      });
      
      if (!res.ok) {
        let errorMessage = 'Failed to upload picture';
        try {
          const errorData = await res.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If response is not JSON, use default message
          errorMessage = `Upload failed with status ${res.status}`;
        }
        throw new Error(errorMessage);
      }
      
      // Success - notify NavBar to update immediately
      if (dataUrl) {
        window.dispatchEvent(new CustomEvent('profilePictureUpdated', { detail: { dataUrl } }));
      }
    } catch (err) {
      setError(err.message || 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  // Email validation
  const validateEmail = (email) => {
    if (!email) return true; // Allow empty email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation for US numbers
  const validatePhone = (phone, code) => {
    if (!phone) return true; // Allow empty phone
    // For US/Canada (+1), require exactly 10 digits
    if (code === '+1') {
      const digitsOnly = phone.replace(/\D/g, '');
      return digitsOnly.length === 10;
    }
    return true; // No validation for other countries
  };

  // Clear success state when field changes
  const handleFieldChange = (fieldName, value, setter) => {
    // Remove spaces from social media handles
    if (fieldName === 'twitter_handle' || fieldName === 'bluesky_handle' || fieldName === 'instagram_handle') {
      value = value.replace(/\s/g, '');
    }
    
    setter(value);
    if (successField === fieldName) {
      setSuccessField(null);
    }
    
    // Validate email field
    if (fieldName === 'contact_email') {
      if (value && !validateEmail(value)) {
        setValidationErrors(prev => ({ ...prev, contact_email: 'Please enter a valid email address' }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.contact_email;
          return newErrors;
        });
      }
    }

    // Validate phone field
    if (fieldName === 'phone') {
      if (value && !validatePhone(value, countryCode)) {
        setValidationErrors(prev => ({ ...prev, phone: 'US phone numbers must be exactly 10 digits' }));
      } else {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.phone;
          return newErrors;
        });
      }
    }
  };

  // Show loading spinner while profile data loads
  if (loadingProfile) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh'
      }}>
        <LoadingSpinner message="Loading profile..." />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const fieldStyle = {
    width: '100%',
    padding: '0.75rem',
    paddingRight: '110px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    fontSize: '1rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '600',
    color: '#333'
  };

  const fieldContainerStyle = {
    marginBottom: '1.5rem'
  };

  const inputWrapperStyle = {
    position: 'relative'
  };

  const buttonStyle = (isActive, isSuccess) => ({
    position: 'absolute',
    right: '6px',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: '0.5rem 1rem',
    backgroundColor: isActive ? '#ccc' : (isSuccess ? '#4caf50' : '#38b44a'),
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: isActive ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.2s',
    whiteSpace: 'nowrap',
    opacity: isActive && !isSuccess ? 0.6 : 1
  });


  return (
    <div style={{ 
      maxWidth: '700px', 
      margin: '0 auto', 
      padding: '2rem 1rem' 
    }}>
      <h1 style={{ 
        fontSize: '2.5rem', 
        fontWeight: '700', 
        color: '#333',
        marginBottom: '0.5rem'
      }}>
        Your Profile
      </h1>
      <p style={{ 
        fontSize: '1rem', 
        color: '#666',
        marginBottom: '2rem'
      }}>
        This optional information is visible to other Study Hall members
      </p>

      {/* Profile Picture Section - Completely Separate */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <label style={labelStyle}>Profile Picture</label>
        <div style={{
          marginTop: '1rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            overflow: 'hidden',
            border: '3px solid #38b44a',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {loadingPicture ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%'
              }}>
                <LoadingSpinner size="small" message="" />
              </div>
            ) : profilePicture ? (
              <img 
                src={profilePicture} 
                alt="Profile" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="80" 
                height="80" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#ccc" 
                strokeWidth="2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingPicture}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: uploadingPicture ? '#ccc' : '#38b44a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: uploadingPicture ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '600',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => {
              if (!uploadingPicture) {
                e.target.style.backgroundColor = '#2d8f3a';
              }
            }}
            onMouseOut={(e) => {
              if (!uploadingPicture) {
                e.target.style.backgroundColor = '#38b44a';
              }
            }}
          >
            {uploadingPicture ? 'Uploading...' : (profilePicture ? 'Change Picture' : 'Upload Picture')}
          </button>
          <small style={{ color: '#999', fontSize: '0.875rem' }}>
            JPG, PNG or GIF. Max size 5MB.
          </small>
        </div>
      </div>

      {/* Main Profile Form */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
          {/* Error Message */}
          {error && (
            <div style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '1rem',
              borderRadius: '6px',
              marginBottom: '1.5rem',
              border: '1px solid #ef5350'
            }}>
              {error}
            </div>
          )}

          {/* Display Name */}
          <div style={fieldContainerStyle}>
            <label style={labelStyle}>Display Name</label>
            <div style={inputWrapperStyle}>
              <input
                type="text"
                value={displayName}
                onChange={(e) => handleFieldChange('display_name', e.target.value, setDisplayName)}
                placeholder="Enter your display name"
                style={fieldStyle}
                onFocus={(e) => e.target.style.borderColor = '#38b44a'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              <button
                onClick={() => updateField('display_name', displayName)}
                disabled={savingField === 'display_name' || !hasChanged('display_name', displayName)}
                style={buttonStyle(savingField === 'display_name' || !hasChanged('display_name', displayName), successField === 'display_name')}
                onMouseOver={(e) => {
                  if (hasChanged('display_name', displayName) && savingField !== 'display_name' && successField !== 'display_name') {
                    e.target.style.backgroundColor = '#2d8f3a';
                  }
                }}
                onMouseOut={(e) => {
                  if (hasChanged('display_name', displayName) && savingField !== 'display_name' && successField !== 'display_name') {
                    e.target.style.backgroundColor = '#38b44a';
                  }
                }}
              >
                {savingField === 'display_name' ? 'Saving...' : (successField === 'display_name' ? '✓ Saved' : 'Update')}
              </button>
            </div>
          </div>

          {/* Twitter Handle */}
          <div style={fieldContainerStyle}>
            <label style={labelStyle}>X / Twitter Handle</label>
            <div style={inputWrapperStyle}>
              <input
                type="text"
                value={twitterHandle}
                onChange={(e) => handleFieldChange('twitter_handle', e.target.value, setTwitterHandle)}
                placeholder="@username"
                style={fieldStyle}
                onFocus={(e) => e.target.style.borderColor = '#38b44a'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              <button
                onClick={() => updateField('twitter_handle', twitterHandle)}
                disabled={savingField === 'twitter_handle' || !hasChanged('twitter_handle', twitterHandle)}
                style={buttonStyle(savingField === 'twitter_handle' || !hasChanged('twitter_handle', twitterHandle), successField === 'twitter_handle')}
                onMouseOver={(e) => {
                  if (hasChanged('twitter_handle', twitterHandle) && savingField !== 'twitter_handle' && successField !== 'twitter_handle') {
                    e.target.style.backgroundColor = '#2d8f3a';
                  }
                }}
                onMouseOut={(e) => {
                  if (hasChanged('twitter_handle', twitterHandle) && savingField !== 'twitter_handle' && successField !== 'twitter_handle') {
                    e.target.style.backgroundColor = '#38b44a';
                  }
                }}
              >
                {savingField === 'twitter_handle' ? 'Saving...' : (successField === 'twitter_handle' ? '✓ Saved' : 'Update')}
              </button>
            </div>
          </div>

          {/* Bluesky Handle */}
          <div style={fieldContainerStyle}>
            <label style={labelStyle}>Bluesky Handle</label>
            <div style={inputWrapperStyle}>
              <input
                type="text"
                value={blueskyHandle}
                onChange={(e) => handleFieldChange('bluesky_handle', e.target.value, setBlueskyHandle)}
                placeholder="@username.bsky.social"
                style={fieldStyle}
                onFocus={(e) => e.target.style.borderColor = '#38b44a'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              <button
                onClick={() => updateField('bluesky_handle', blueskyHandle)}
                disabled={savingField === 'bluesky_handle' || !hasChanged('bluesky_handle', blueskyHandle)}
                style={buttonStyle(savingField === 'bluesky_handle' || !hasChanged('bluesky_handle', blueskyHandle), successField === 'bluesky_handle')}
                onMouseOver={(e) => {
                  if (hasChanged('bluesky_handle', blueskyHandle) && savingField !== 'bluesky_handle' && successField !== 'bluesky_handle') {
                    e.target.style.backgroundColor = '#2d8f3a';
                  }
                }}
                onMouseOut={(e) => {
                  if (hasChanged('bluesky_handle', blueskyHandle) && savingField !== 'bluesky_handle' && successField !== 'bluesky_handle') {
                    e.target.style.backgroundColor = '#38b44a';
                  }
                }}
              >
                {savingField === 'bluesky_handle' ? 'Saving...' : (successField === 'bluesky_handle' ? '✓ Saved' : 'Update')}
              </button>
            </div>
          </div>

          {/* Instagram Handle */}
          <div style={fieldContainerStyle}>
            <label style={labelStyle}>Instagram Handle</label>
            <div style={inputWrapperStyle}>
              <input
                type="text"
                value={instagramHandle}
                onChange={(e) => handleFieldChange('instagram_handle', e.target.value, setInstagramHandle)}
                placeholder="@username"
                style={fieldStyle}
                onFocus={(e) => e.target.style.borderColor = '#38b44a'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              <button
                onClick={() => updateField('instagram_handle', instagramHandle)}
                disabled={savingField === 'instagram_handle' || !hasChanged('instagram_handle', instagramHandle)}
                style={buttonStyle(savingField === 'instagram_handle' || !hasChanged('instagram_handle', instagramHandle), successField === 'instagram_handle')}
                onMouseOver={(e) => {
                  if (hasChanged('instagram_handle', instagramHandle) && savingField !== 'instagram_handle' && successField !== 'instagram_handle') {
                    e.target.style.backgroundColor = '#2d8f3a';
                  }
                }}
                onMouseOut={(e) => {
                  if (hasChanged('instagram_handle', instagramHandle) && savingField !== 'instagram_handle' && successField !== 'instagram_handle') {
                    e.target.style.backgroundColor = '#38b44a';
                  }
                }}
              >
                {savingField === 'instagram_handle' ? 'Saving...' : (successField === 'instagram_handle' ? '✓ Saved' : 'Update')}
              </button>
            </div>
          </div>

          {/* Contact Email */}
          <div style={fieldContainerStyle}>
            <label style={labelStyle}>Contact Email</label>
            <div style={inputWrapperStyle}>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => handleFieldChange('contact_email', e.target.value, setContactEmail)}
                placeholder="your@email.com"
                style={{
                  ...fieldStyle,
                  borderColor: validationErrors.contact_email ? '#dc3545' : '#ddd'
                }}
                onFocus={(e) => {
                  if (!validationErrors.contact_email) {
                    e.target.style.borderColor = '#38b44a';
                  }
                }}
                onBlur={(e) => {
                  if (!validationErrors.contact_email) {
                    e.target.style.borderColor = '#ddd';
                  }
                }}
              />
              <button
                onClick={() => updateField('contact_email', contactEmail)}
                disabled={savingField === 'contact_email' || !hasChanged('contact_email', contactEmail) || validationErrors.contact_email}
                style={buttonStyle(savingField === 'contact_email' || !hasChanged('contact_email', contactEmail) || validationErrors.contact_email, successField === 'contact_email')}
                onMouseOver={(e) => {
                  if (hasChanged('contact_email', contactEmail) && !validationErrors.contact_email && savingField !== 'contact_email' && successField !== 'contact_email') {
                    e.target.style.backgroundColor = '#2d8f3a';
                  }
                }}
                onMouseOut={(e) => {
                  if (hasChanged('contact_email', contactEmail) && !validationErrors.contact_email && savingField !== 'contact_email' && successField !== 'contact_email') {
                    e.target.style.backgroundColor = '#38b44a';
                  }
                }}
              >
                {savingField === 'contact_email' ? 'Saving...' : (successField === 'contact_email' ? '✓ Saved' : 'Update')}
              </button>
            </div>
            {validationErrors.contact_email && (
              <small style={{ color: '#dc3545', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                {validationErrors.contact_email}
              </small>
            )}
          </div>

          {/* Phone */}
          <div className="profile-phone-container" style={fieldContainerStyle}>
            <label style={labelStyle}>Phone Number</label>
            <div className="profile-phone-input-group" style={{ display: 'flex', gap: '0.5rem', position: 'relative' }}>
              <select
                className="profile-country-code-select"
                value={countryCode}
                onChange={(e) => {
                  const newCode = e.target.value;
                  setCountryCode(newCode);
                  if (successField === 'phone') {
                    setSuccessField(null);
                  }
                  // Re-validate phone with new country code
                  if (phone && !validatePhone(phone, newCode)) {
                    setValidationErrors(prev => ({ ...prev, phone: 'US phone numbers must be exactly 10 digits' }));
                  } else {
                    setValidationErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.phone;
                      return newErrors;
                    });
                  }
                }}
                style={{
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '1rem',
                  width: '120px',
                  cursor: 'pointer'
                }}
              >
                {countryCodes.map((cc, index) => (
                  <option key={`${cc.code}-${cc.country}-${index}`} value={cc.code}>
                    {cc.flag} {cc.code}
                  </option>
                ))}
              </select>
              <div className="profile-phone-input-wrapper" style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                <input
                  type="tel"
                  className="profile-phone-input"
                  value={phone}
                  onChange={(e) => handleFieldChange('phone', e.target.value, setPhone)}
                  placeholder="123-456-7890"
                  style={{
                    ...fieldStyle,
                    borderColor: validationErrors.phone ? '#dc3545' : '#ddd'
                  }}
                  onFocus={(e) => {
                    if (!validationErrors.phone) {
                      e.target.style.borderColor = '#38b44a';
                    }
                  }}
                  onBlur={(e) => {
                    if (!validationErrors.phone) {
                      e.target.style.borderColor = '#ddd';
                    }
                  }}
                />
                <button
                  className="profile-phone-update-button"
                  onClick={() => updateField('phone', phone)}
                  disabled={savingField === 'phone' || !hasChanged('phone', phone) || validationErrors.phone}
                  style={buttonStyle(savingField === 'phone' || !hasChanged('phone', phone) || validationErrors.phone, successField === 'phone')}
                  onMouseOver={(e) => {
                    if (hasChanged('phone', phone) && !validationErrors.phone && savingField !== 'phone' && successField !== 'phone') {
                      e.target.style.backgroundColor = '#2d8f3a';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (hasChanged('phone', phone) && !validationErrors.phone && savingField !== 'phone' && successField !== 'phone') {
                      e.target.style.backgroundColor = '#38b44a';
                    }
                  }}
                >
                  {savingField === 'phone' ? 'Saving...' : (successField === 'phone' ? '✓ Saved' : 'Update')}
                </button>
              </div>
            </div>
            {validationErrors.phone && (
              <small style={{ color: '#dc3545', fontSize: '0.875rem', display: 'block', marginTop: '0.25rem' }}>
                {validationErrors.phone}
              </small>
            )}
          </div>

          {/* Bio */}
          <div style={fieldContainerStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={labelStyle}>Bio</label>
              <button
                onClick={() => updateField('bio', bio)}
                disabled={savingField === 'bio' || !hasChanged('bio', bio)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: savingField === 'bio' || !hasChanged('bio', bio) ? '#ccc' : (successField === 'bio' ? '#4caf50' : '#38b44a'),
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: savingField === 'bio' || !hasChanged('bio', bio) ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                  whiteSpace: 'nowrap',
                  opacity: savingField === 'bio' || !hasChanged('bio', bio) ? 0.6 : 1
                }}
                onMouseOver={(e) => {
                  if (hasChanged('bio', bio) && savingField !== 'bio' && successField !== 'bio') {
                    e.target.style.backgroundColor = '#2d8f3a';
                  }
                }}
                onMouseOut={(e) => {
                  if (hasChanged('bio', bio) && savingField !== 'bio' && successField !== 'bio') {
                    e.target.style.backgroundColor = '#38b44a';
                  }
                }}
              >
                {savingField === 'bio' ? 'Saving...' : (successField === 'bio' ? '✓ Saved' : 'Update')}
              </button>
            </div>
            
            {/* Editor Toolbar */}
            {editor && (
              <div style={{
                border: '1px solid #ddd',
                borderBottom: 'none',
                borderRadius: '4px 4px 0 0',
                backgroundColor: '#f8f9fa',
                padding: '0.5rem',
                display: 'flex',
                gap: '0.25rem',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  disabled={!editor.can().chain().focus().toggleBold().run()}
                  style={{
                    padding: '0.375rem 0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '3px',
                    backgroundColor: editor.isActive('bold') ? '#38b44a' : 'white',
                    color: editor.isActive('bold') ? 'white' : '#333',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.875rem'
                  }}
                  type="button"
                >
                  B
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  disabled={!editor.can().chain().focus().toggleItalic().run()}
                  style={{
                    padding: '0.375rem 0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '3px',
                    backgroundColor: editor.isActive('italic') ? '#38b44a' : 'white',
                    color: editor.isActive('italic') ? 'white' : '#333',
                    cursor: 'pointer',
                    fontStyle: 'italic',
                    fontSize: '0.875rem'
                  }}
                  type="button"
                >
                  I
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  disabled={!editor.can().chain().focus().toggleStrike().run()}
                  style={{
                    padding: '0.375rem 0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '3px',
                    backgroundColor: editor.isActive('strike') ? '#38b44a' : 'white',
                    color: editor.isActive('strike') ? 'white' : '#333',
                    cursor: 'pointer',
                    textDecoration: 'line-through',
                    fontSize: '0.875rem'
                  }}
                  type="button"
                >
                  S
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  style={{
                    padding: '0.375rem 0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '3px',
                    backgroundColor: editor.isActive('heading', { level: 2 }) ? '#38b44a' : 'white',
                    color: editor.isActive('heading', { level: 2 }) ? 'white' : '#333',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                  type="button"
                >
                  H2
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  style={{
                    padding: '0.375rem 0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '3px',
                    backgroundColor: editor.isActive('bulletList') ? '#38b44a' : 'white',
                    color: editor.isActive('bulletList') ? 'white' : '#333',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                  type="button"
                >
                  • List
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  style={{
                    padding: '0.375rem 0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '3px',
                    backgroundColor: editor.isActive('orderedList') ? '#38b44a' : 'white',
                    color: editor.isActive('orderedList') ? 'white' : '#333',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                  type="button"
                >
                  1. List
                </button>
              </div>
            )}
            
            {/* Editor Content */}
            <div style={{ 
              border: '1px solid #ddd',
              borderRadius: editor ? '0 0 4px 4px' : '4px',
              backgroundColor: 'white',
              minHeight: '200px',
              padding: '0.75rem'
            }}>
              <EditorContent 
                editor={editor}
                style={{
                  minHeight: '200px',
                  fontFamily: 'inherit',
                  outline: 'none'
                }}
              />
            </div>
          </div>
        </div>
    </div>
  );
}
